import { Injectable, inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthStore } from '@huddle-sports-platform/app-core';

import { AccountStore } from './account-store';
import { AdminInviteStore } from './admin-invite-store';
import { AppModeStore } from './app-mode';
import { ClubAdmin } from './club-admin';
import { DataInit } from './data-init';
import { MemberDataStore } from './member-data';
import { NotificationAccess } from './notification-access';
import { ThemeAsk } from './theme-ask';

/** The very first ask a fresh sign-up meets: which look is theirs. */
export const CHOOSE_THEME = '/choose-theme';
/** The step before ANY of the below: who you are. */
export const COMPLETE_YOUR_PROFILE = '/complete-your-profile';
/** The step after the profile form: what they are TO the club. */
export const YOUR_ROLE = '/your-role';
/** Where an ordinary user of the club belongs. */
export const MEMBER_HOME = '/tabs/home';
/** The member app's settings tab - where My account hangs. */
export const MEMBER_SETTINGS = '/settings';
/** Where somebody who runs the club belongs. */
export const ADMIN_HOME = '/admin';
/** The one thing that comes BEFORE either: an unanswered offer. */
export const ADMIN_INVITE = '/admin-invite';
/** The ask between the offer and the apps: notifications, while the
 *  system would still show its sheet. */
export const ENABLE_NOTIFICATIONS = '/enable-notifications';
/** The guided payments checklist - reached from the admin Finance screen's
 *  "Get up & running" rows and the status banner, never forced. */
export const SETUP_YOUR_PAYMENTS = '/setup-your-payments';
/** The provider's embedded form, on its own page under a slim bar. */
export const CONNECT_YOUR_BANK = '/connect-your-bank-account';
/** Huddle's terms with whoever runs the club - read and accepted before
 *  the bank flow will open. */
export const ADMIN_TERMS_ROUTE = '/admin-terms';
/** The signed-in person's own details. */
export const MY_ACCOUNT = '/my-account';
/** The writable face of My account - names and date of birth. */
export const EDIT_ACCOUNT = '/edit-account';
/** The follower landing - a login with no registrations yet. */
export const WELCOME = '/welcome';
/** The reauthentication gate in front of account deletion. */
export const DELETE_ACCOUNT = '/delete-account';
/** Where a signed-out visitor belongs: the door. */
export const SIGN_IN = '/sign-in';

/**
 * The one answer to "where do they go now?".
 *
 * Every door (sign-in, the sign-up fold-down, the profile form, the
 * notifications ask) ends by sending somebody INTO the app - and `/`
 * itself asks on every refresh, so the FIRST answer is presence: signed
 * out goes to the sign-in door. For the signed in:
 *
 * 1. No name or date of birth saved -> the profile form, IMMEDIATELY after
 *    data-init and ahead of everything else. Every screen past this point
 *    addresses somebody by name, files them by age grade, or hands them a
 *    role - all of which read a person, not a login. It is an ASK, not a
 *    gate: the page's back button waves it away for the session (see
 *    AccountStore.shouldAskProfile), and nothing re-asks until the next
 *    run. What a half-named account cannot DO is enforced where the
 *    consequence is - joinClub refuses one server-side, and the welcome
 *    page replaces its Register button with the nudge.
 * 2. The member row exists but names no member types -> the role step.
 *    joinClub opens that row with an empty list, so "empty" means the
 *    question has never been put. It sits here, right behind the profile,
 *    because plan criteria and the roster both read the answer.
 * 3. A role is waiting to be accepted -> the ASK. Meeting the invitation is
 *    part of signing up, not something to run into later: somebody who has
 *    just created an account should be asked immediately, rather than
 *    landing in the member app and only finding out on a reload.
 * 4. The device would still show its notifications sheet -> the enable
 *    screen (data-init settles the answer; 'not now' skips for the
 *    session).
 * 5. They run the club AND are in admin mode -> the admin screen, always.
 *    An unfinished club is not diverted anywhere: its Finance screen
 *    leads with the "Get up & running" checklist and the payments banner
 *    (docs/club-onboarding.md).
 * 6. Otherwise -> the member tabs - the member landing.
 *
 * There is NO membership gate - but there IS a follower landing. An
 * account with no registrations at all lands on /welcome: the club's
 * colour, its crest, and the one door in (register). The member tabs
 * stay reachable - it is where the journey starts, not a hold. One
 * registration anywhere on the account (self or a managed child) and
 * home is home: a parent with a registered child is not welcomed like a
 * stranger. Waiting behind a HOLDING page for an admin to add you was a
 * dead end for exactly the people a club most wants to reach - parents,
 * ex-players, supporters (docs/registration.md).
 *
 * Deciding HERE rather than leaning on `adminInviteGuard` is what makes (2)
 * reliable. The guard is the catch-all for every other way into the app,
 * but a door has just finished onboarding somebody and can afford to wait
 * for the answer before choosing where to send them.
 *
 * NONE of these is a lock. Both apps stay reachable by URL whatever the
 * mode says, an admin who walks into the member tabs is not bounced back
 * out of them, and rule 1 asks once rather than walling anything off.
 */
@Injectable({ providedIn: 'root' })
export class Landing {
  private readonly account = inject(AccountStore);
  private readonly clubAdmin = inject(ClubAdmin);
  private readonly invites = inject(AdminInviteStore);
  private readonly appMode = inject(AppModeStore);
  private readonly auth = inject(AuthStore);
  private readonly dataInit = inject(DataInit);
  private readonly memberData = inject(MemberDataStore);
  private readonly notifications = inject(NotificationAccess);
  private readonly themeAsk = inject(ThemeAsk);

  /**
   * ONE await - data initialisation (data-init.ts) - then pure reads.
   * Guessing either answer opens the wrong app: the member tabs for
   * whoever runs the club, or a swallowed invitation for somebody who was
   * just made an admin.
   */
  async url(): Promise<string> {
    // Signed OUT -> the door. Auth must RESOLVE first: at boot the
    // persisted session restores asynchronously, and reading user()
    // before that would send a member to sign-in on every refresh. This
    // branch is what keeps `/` answering for visitors at all -
    // DataInit.whenReady() waits for a uid that a signed-out visitor
    // will never have.
    await this.auth.whenReady();
    if (!this.auth.user()) return SIGN_IN;
    await this.dataInit.whenReady();
    // The LOOK before anything: a one-tap ask with the page as its own
    // preview, asked exactly once per device (the stored choice is the
    // state - data/theme-ask.ts). First because every screen after it,
    // this flow's own pages included, is better read in the theme the
    // person actually wants.
    if (this.themeAsk.shouldAsk()) return CHOOSE_THEME;
    // WHO before what. data-init has just settled the account snapshot, so
    // this reads the real answer - and it comes before the invitation, the
    // notifications ask and both apps, because every one of them speaks to
    // a named person.
    if (this.account.shouldAskProfile()) return COMPLETE_YOUR_PROFILE;
    // WHO, then WHAT-THEY-ARE-HERE. An empty memberTypes means the question
    // has never been answered - joinClub opens the row with [], and the
    // profile form is the step that just ran it. Guarded on the row being
    // LOADED: `undefined` is "not warmed yet", which is not the same as
    // "unanswered", and sending somebody to the role page on a slow warm
    // would ask a member who answered months ago.
    const myRow = this.memberData.me();
    if (myRow && myRow.memberTypes.length === 0) return YOUR_ROLE;
    if (this.invites.pending()) return ADMIN_INVITE;
    // Who you are in the club comes first; what the app may send you,
    // second; the app itself, last. Deciding HERE (not only in the /tabs
    // guard) is what puts the ask in front of ADMINS too - their landing
    // never passes the tabs chain.
    if (this.notifications.shouldAsk()) return ENABLE_NOTIFICATIONS;
    if (this.appMode.mode() === 'admin') return ADMIN_HOME;
    // Nobody on this account registered yet - follower territory, and the
    // welcome page is the marketing moment plus the one door in. A parent
    // with a registered child skips it: their landing is the family home.
    if (this.memberData.registrations().length === 0) return WELCOME;
    return MEMBER_HOME;
  }

}

/**
 * The app's front door: `/` asks {@link Landing} where to go.
 *
 * Without it a REFRESH never consults any of this - the URL simply said
 * `/tabs/home` and that is where the browser went, so an admin reloading
 * the app landed in the member view every time. The doors decide for the
 * navigations they make; this decides for the ones the browser makes.
 */
export const landingGuard: CanActivateFn = async () => {
  const router = inject(Router);
  return router.parseUrl(await inject(Landing).url());
};

